/**
 * 监管大屏的**演示虚拟数据模块**(Y-34,2026-10-16 人裁决;S-50 扩到两块板)。
 *
 * ═══ 它为什么存在 ═══
 *
 * 画板把大屏画成一个「船舶世界」:在航船舶 42、内锚地锚泊 82、外锚地锚泊 42、
 * 今日外轮代理单 86、六类事件计数、船位 / 航线 / 走私预警、六个部门的视频接入。
 * 这些东西在本平台**都没有数据模型**:没有船舶表、没有 AIS 数据源、
 * 「外轮代理单」是平台边界外的业务(M-09 里没有它)、视频整块不在本期
 * (L0-06 搁置、M-09 排除、BLK-02 未解除)。
 *
 * 2026-10-16 人裁决:两块大屏的船舶世界用**虚拟数据填充** —— 不许编造接口,
 * 但可以把演示画面做完整;处置纪律三条写死在这里:
 *
 *   ① **确定性固定值,不用随机数**:下面每个数字都逐字抄自画板(design.pen 的
 *      h7V8I / tOqVP 两块板),每次打开大屏看到的是同一组数,演示可复现。
 *   ② **集中在这一个模块**:视图里 import 的演示数据只来自这里;
 *      将来接真实数据源(甲方口径待给出)时,换掉这一个文件,视图不动。
 *   ③ **不许驱逐真实数据**:两块大屏上真数落在
 *      「G1 订单详情卡(最近一单的真实订单字段)」「两组东八区时钟」
 *      「订单口径(G1 的页脚 / G5 侧栏小卡:区间的单量 / 架次 / 在途,视图里现取)」;
 *      它们一个都不许被本模块的虚拟值顶掉。
 *
 * ═══ 它装什么、不装什么 ═══
 *
 * 只装**数值与点位名这类数据**。页面上的静态文案(KPI 标签与口径小字、事件名、
 * 部门名、标题)住在各页自己的视图文件里,由 design-copy 的
 * 「v2 · G1 监管驾驶舱」与「v2 · G5 监管端综合监控」两帧逐字守 ——
 * 那句话必须落在一个文件里,数据不是文案。
 *
 * ═══ G1 与 G5 共用一个模块而不是两份 ═══
 *
 * S-50(G5 综合监控)的 KPI 四个数、六类事件计数、六路视频格与六部门接入灯
 * 与 G1 逐值相同(两板共享同一组演示值),船舶运行状态、船舶列表、地图标点、
 * 天气与底行事件行是 G5 新增的。**同一个值有两份定义时,改一处漏一处不会报错,
 * 只会让两块屏对不上** —— 所以 G5 的数据并进这个模块(section 标注),不另开一份。
 *
 * ═══ 它替换掉的与不替换的 ═══
 *
 * G1 的地图整块(群岛、航线、船位、预警圈、视频联动卡)用的是从设计稿导出的
 * 烘好的画布 public/g1-map-canvas.png —— 那是**像素**不是数据,这里没有、也不需要坐标。
 * G5 的地图不是烘好的画布:它是底图 public/g5-map.png(本地演示位图)+ DOM 标注层,
 * 所以**标点坐标真的在这里**(G5_DEMO_MAP 的 x/y,画板写死的像素位,见下)。
 *
 * ═══ 六路视频格的实拍图 ═══
 *
 * 画板挂的是 Unsplash 外链;实现下载成 480×600 的本地图
 * (public/video-ch-0N.jpg,裁成竖构图)—— 演示环境不许依赖外网。
 * 图是**演示占位**,不是任何一路真实推流(Y-33)。
 */

/**
 * 四张 KPI 卡的数字。键与 DashboardView.vue(G1)那四张卡一一对应,
 * **G5 的 KPI 行逐值复用同一组**(两块大屏共享,见文件头「共用一个模块」一段):
 * 在航船舶 42 / 内锚地锚泊 82 / 外锚地锚泊 42 / 今日外轮代理单 86 —— 画板原值。
 *
 * @unimplemented(reason="演示虚拟数据,不接真实数据源(甲方口径待给出) —— 在航/锚泊船舶数没有数据模型也没有 AIS 来源,「外轮代理单」是平台边界外的业务(M-09)。按 Y-34 的裁决用画板原值填充,确定性固定值、不用随机", owner="me", blocks="G1 / G5 两块大屏的 KPI 数字")
 */
export const G1_DEMO_KPI = {
  /** 在航船舶(画板:42) */
  inVoyage: 42,
  /** 内锚地锚泊(画板:82) */
  innerAnchorage: 82,
  /** 外锚地锚泊(画板:42) */
  outerAnchorage: 42,
  /** 今日外轮代理单(画板:86) */
  agencyToday: 86,
} as const

/**
 * 六类事件的计数(画板原值:128 / 96 / 3 / 42 / 27 / 5)。
 * G1 用它喂底栏六张事件卡;**G5 用它喂底行六枚统计块**(逐值共享,见文件头)。
 * 键与视图里那六张卡一一对应;名字住在各页的视图里(文案)。
 *
 * @unimplemented(reason="演示虚拟数据,不接真实数据源(甲方口径待给出) —— 这六类事件在库里没有数据模型:现有的审计日志是运营端事件流(另一张事件表),与这六类无一对应。按 Y-34 的裁决用画板原值填充,确定性固定值", owner="me", blocks="G1 底栏 / G5 底行两处统计的计数")
 */
export const G1_DEMO_EVENTS = {
  /** 船舶事件 */
  ship: 128,
  /** 视频监管事件 */
  videoMonitor: 96,
  /** 走私监管预警 */
  smuggling: 3,
  /** 海关验放存档 */
  customs: 42,
  /** 边检登轮记录 */
  immigration: 27,
  /** AIS 信号告警 */
  aisAlarm: 5,
} as const

/** 六路直播格的演示数据:通道号 + 点位名 + 徽标种类 + 本地占位图 */
export interface G1DemoVideoCell {
  /** 通道号(CH-01…CH-06,画板原值) */
  readonly ch: string
  /** 点位名(画板原值;点名本身是数据,名字不锁) */
  readonly point: string
  /** 徽标种类。**小写机器取值** —— 'LIVE' / 'REC' 两个词是画板锁定的文案,住在视图里 */
  readonly badge: 'live' | 'rec'
  /** 本地占位图(public/ 下的相对路径) */
  readonly img: string
}

/**
 * 六路视频格(画板:两行 × 三格)。
 *
 * @unimplemented(reason="按甲方 2026-10-14 口径,演示占位,不接真实视频流/真实系统 —— 摄像头点位没有数据模型,视频整块不在本期(L0-06 搁置、M-09 排除、BLK-02 未解除);实拍图是本地演示资源,不是任何一路推流(Y-33)", owner="me", blocks="G1 六路视频格的画面与通道")
 */
export const G1_DEMO_VIDEO_CELLS: readonly G1DemoVideoCell[] = [
  { ch: 'CH-01', point: '普陀山锚地 · 东', badge: 'live', img: 'video-ch-01.jpg' },
  { ch: 'CH-02', point: '定海码头 · 北', badge: 'rec', img: 'video-ch-02.jpg' },
  { ch: 'CH-03', point: '六横外锚地', badge: 'rec', img: 'video-ch-03.jpg' },
  { ch: 'CH-04', point: '岱山锚地 · 西', badge: 'live', img: 'video-ch-04.jpg' },
  { ch: 'CH-05', point: '临城起降场', badge: 'rec', img: 'video-ch-05.jpg' },
  { ch: 'CH-06', point: '衢山锚地', badge: 'rec', img: 'video-ch-06.jpg' },
]

/**
 * 六个部门的接入状态(画板:海事/海关/边检/港航/应急 绿灯,公安 黄灯)。
 * 名字住在视图里(文案),这里只给机器键与灯色。
 *
 * @unimplemented(reason="演示虚拟数据,不接真实数据源(甲方口径待给出) —— N-02 明确不与海关/边检的情报或申报系统做任何对接,AdminAccount 也没有部门维度;六个接入位是甲方口径的演示占位(Y-33)", owner="me", blocks="G1「多部门监管仓」的六部门接入灯")
 */
export const G1_DEMO_DEPARTMENTS: readonly { readonly key: string; readonly tone: 'ok' | 'warn' }[] = [
  { key: 'msa', tone: 'ok' },
  { key: 'customs', tone: 'ok' },
  { key: 'immigration', tone: 'ok' },
  { key: 'port', tone: 'ok' },
  { key: 'police', tone: 'warn' },
  { key: 'emergency', tone: 'ok' },
]

/**
 * 顶栏「系统运行正常」的状态位(画板:绿灯常态)。
 *
 * @unimplemented(reason="演示虚拟数据,不接真实数据源(甲方口径待给出) —— 契约里没有任何健康检查接口。按 2026-10-16 人裁决(Y-34)连同船舶世界一起做演示固定值:它不反映真实连通性(连不上 api 时订单卡会显著报错,这一行不会变)", owner="me", blocks="G1 顶栏「系统运行正常」")
 */
export const G1_DEMO_SYSTEM_STATUS = 'normal' as const

// ═══════════════════════════════════════════════════════════
// G5 · 监管端综合监控(design.pen tOqVP,S-50)新增的演示数据
//
// 与 G1 同款的三条纪律(见文件头):确定性固定值、只来这里取、不驱逐真数。
// 下面每个数字 / 名称 / 点位都逐字抄自画板与渲染图(docs/superpowers/notes/
// 2026-10-16-v2-ops-gov-画板抄录/G5-监管端综合监控.md 的「数据元素」段)。
// ═══════════════════════════════════════════════════════════

/**
 * 「运行状态」卡四行的计数(画板原值:运行中 36 / 准备起降 8 / 预警告警 3 / 不在飞 5)。
 * 键是**机器取值**,汉字与灯色住在 MonitorView.vue(SHIP_STATUS 单点)—— 同 G1 视频格的分法。
 *
 * @unimplemented(reason="演示虚拟数据,不接真实数据源(甲方口径待给出) —— 无人机运行态在本平台没有数据模型(没有机队表、没有 AIS/ADS-B 来源)。按 Y-34 的口径用画板原值填充,确定性固定值、不用随机", owner="me", blocks="G5「运行状态」卡的四行计数")
 */
export const G5_DEMO_RUN_STATUS = {
  /** 运行中(画板:36) */
  running: 36,
  /** 准备起降(画板:8) */
  landing: 8,
  /** 预警告警(画板:3) */
  alert: 3,
  /** 不在飞(画板:5) */
  idle: 5,
} as const

/** 一艘在航船舶的演示行:船名 + 航向 + 速度 + 机器状态位 */
export interface G5DemoShip {
  /** 船名(画板原值) */
  readonly name: string
  /** 航向(画板原值,含度数符号) */
  readonly heading: string
  /** 速度(画板原值,含单位) */
  readonly speed: string
  /** 状态机器取值:running / landing / alert / anchored —— 汉字住在 MonitorView.vue */
  readonly status: 'running' | 'landing' | 'alert' | 'anchored'
}

/**
 * 船舶列表五行(画板逐字:远洋之星 · 078° · 28 km/h · 运行中 / 海运先锋 · 120° · 24 km/h · 准备起降 /
 * 东海明珠 · 305° · 18 km/h · 预警告警 / 海天一号 · 090° · 20 km/h · 运行中 / 蓝海通 · 210° · 0 km/h · 锚泊中)。
 * 画板另有一个搜索框(「搜索船名、航次号、起降场…」)—— 它筛的就是这份固定的五行。
 *
 * @unimplemented(reason="演示虚拟数据,不接真实数据源(甲方口径待给出) —— 没有船舶表、没有 AIS 来源,「航次号」这个概念在平台里不存在。按 Y-34 的口径用画板原值填充,确定性固定值", owner="me", blocks="G5 轨迹面板的船舶列表五行")
 */
export const G5_DEMO_SHIPS: readonly G5DemoShip[] = [
  { name: '远洋之星', heading: '078°', speed: '28 km/h', status: 'running' },
  { name: '海运先锋', heading: '120°', speed: '24 km/h', status: 'landing' },
  { name: '东海明珠', heading: '305°', speed: '18 km/h', status: 'alert' },
  { name: '海天一号', heading: '090°', speed: '20 km/h', status: 'running' },
  { name: '蓝海通', heading: '210°', speed: '0 km/h', status: 'anchored' },
]

/**
 * 地图区的演示标点(全部是**画板写死的像素位**,不是经纬度,也没有投影口径)。
 * 坐标相对地图区左上角;起降场三个点的值逐字抄自抄录件(定海 @330,150 /
 * 临城 @430,260 / 普陀 @560,190),两枚船位与锚位按渲染图对齐。
 * 起降场名字是数据(不是锁定文案);比例尺文字随将来的缩放级别生成,这里先给演示值。
 *
 * @unimplemented(reason="演示虚拟数据,不接真实数据源(甲方口径待给出) —— 底图 public/g5-map.png 是一张本地演示位图(不是瓦片、不是地图引擎渲染),其上的标点是按画板手摆的像素位,没有经纬度来源。按 Y-34 的口径做装饰,真实性等甲方给出地图口径(AS-097)", owner="me", blocks="G5 地图区的三个起降场 / 两枚船位 / 锚位 / 比例尺")
 */
export const G5_DEMO_MAP = {
  /** 三个起降场标点(name 是数据;坐标单位 px) */
  pads: [
    { name: '定海起降场', x: 330, y: 150 },
    { name: '临城起降场', x: 430, y: 260 },
    { name: '普陀起降场', x: 560, y: 190 },
  ],
  /** 两枚船图标的位置(画板:@300,210 / @620,330) */
  ships: [
    { x: 300, y: 210 },
    { x: 620, y: 330 },
  ],
  /** 一枚锚图标(按渲染图对齐) */
  anchor: { x: 315, y: 104 },
  /** 走私预警圈的左上角(圈 120×120,文字标签压在圈下缘) */
  warning: { x: 500, y: 292 },
  /** 比例尺文字(画板与渲染图:0 —— 20 km;将来随缩放级别生成) */
  scaleText: '0 —— 20 km',
} as const

/**
 * 顶栏天气(画板:晴 26°C)。**没有数据源** —— 契约里没有任何天气接口,
 * 仓库里也没有外部天气集成;日期那一格反而是真数(本地时钟,东八区)。
 *
 * @unimplemented(reason="演示虚拟数据,不接真实数据源(甲方口径待给出) —— 天气需要外部供应商,决策表里没有任何相关条目;按 Y-34 的口径做固定演示值(同一格里日期是真数)", owner="me", blocks="G5 顶栏天气「晴 26°C」")
 */
export const G5_DEMO_WEATHER = { text: '晴 26°C' } as const

/** 底行「实时事件」的一行:时刻 + 文本 + 部门机器键 + 点色(semantic tone 名) */
export interface G5DemoEventRow {
  readonly time: string
  readonly text: string
  /** 部门机器键(汉字住在 MonitorView.vue 的 DEPARTMENT_NAMES,与 G1 同款) */
  readonly dept: 'msa' | 'customs' | 'immigration' | 'port' | 'police' | 'emergency'
  /** 点色:tone 名(danger / info / warning / muted) */
  readonly tone: 'danger' | 'info' | 'warning' | 'muted'
}

/**
 * 「实时事件」四条(画板逐字:14:20 临城起降场闯界人员靠近 · 公安 / 13:56 东海明珠偏离航线 · 海事 /
 * 12:41 普陀山锚地异常停留 · 边检 / 09:23 六横岛起降场设备离线 · 港航)。
 * 「部门」这个维度在 AdminAccount 里不存在(N-02)。
 *
 * @unimplemented(reason="演示虚拟数据,不接真实数据源(甲方口径待给出) —— 事件流没有数据模型,部门维度也不存在(AdminAccount 没有部门字段;N-02 明确不与海关/边检系统对接)。按 Y-34 的口径用画板原值填充,确定性固定值", owner="me", blocks="G5 底行「实时事件」四条")
 */
export const G5_DEMO_EVENT_ROWS: readonly G5DemoEventRow[] = [
  { time: '14:20', text: '临城起降场闯界人员靠近', dept: 'police', tone: 'danger' },
  { time: '13:56', text: '东海明珠偏离航线', dept: 'msa', tone: 'info' },
  { time: '12:41', text: '普陀山锚地异常停留', dept: 'immigration', tone: 'warning' },
  { time: '09:23', text: '六横岛起降场设备离线', dept: 'port', tone: 'muted' },
]

/**
 * 六枚统计块的子行(画板逐字;键与 G1_DEMO_EVENTS 的六个键一一对应)。
 * 子行里的分类词与数字都是数据(「航线偏离 12 · 异常停留 18」这类),不是锁定文案 ——
 * 将来接真实数据源时它们由来源给出。子行写「—」的那半格是画板的原样(「设备离线 —」)。
 *
 * @unimplemented(reason="演示虚拟数据,不接真实数据源(甲方口径待给出) —— 六类事件的分类计数(海关/边检/海事/港航/公安的业务数据)没有模型,N-02 排除对接;现有 audit_logs 的事件类型与这六类无一对应。按 Y-34 的口径用画板原值填充,确定性固定值", owner="me", blocks="G5 底行六枚统计块的子行")
 */
export const G5_DEMO_EVENT_SUBLINES = {
  ship: '航线偏离 12 · 异常停留 18',
  videoMonitor: '越界预警 6 · 设备离线 —',
  smuggling: '可疑船只靠近禁航区',
  customs: '报关单 16 · 放行 12',
  immigration: '登船 14 · 离船 12',
  aisAlarm: '信号丢失 2 · 轨迹异常 1',
} as const

/**
 * 「视频留存 & 回看」四个检索行的**演示取值**(全部 / 全选(64 路) / 近 24 小时 / 远洋之星)。
 * 标签(监管单位 / 摄像头点位 / 时间区间 / 关联船名)是锁定文案,住在 MonitorView.vue;
 * 这里只有值。整块是形态占位(Y-33 同款口径),这些值不接任何数据。
 *
 * @unimplemented(reason="按甲方 2026-10-14 口径,演示占位,不接真实视频流/真实系统 —— 「64 路摄像头」「监管单位」这些总量没有任何模型支撑,视频直播/回放整块不在本期(L0-06 搁置、M-09 排除、BLK-02 未解除)", owner="me", blocks="G5「视频留存 & 回看」的四个检索取值")
 */
export const G5_DEMO_VIDEO_SEARCH = {
  agency: '全部',
  camera: '全选(64 路)',
  range: '近 24 小时',
  ship: '远洋之星',
} as const

/**
 * 回看时间轴的四个时刻刻度(画板示例:07:00 / 09:00 / 11:00 / 13:00)。
 * 轨道、已播段与告警段的位置由所选时间区间生成(实现没接数据,先给演示刻度)。
 *
 * @unimplemented(reason="按甲方 2026-10-14 口径,演示占位,不接真实视频流/真实系统 —— 时间轴的刻度来自所选区间,而区间与回放都还没有数据源(L0-06 搁置、M-09 排除)", owner="me", blocks="G5「视频留存 & 回看」的时间轴刻度")
 */
export const G5_DEMO_TIMELINE_TICKS: readonly string[] = ['07:00', '09:00', '11:00', '13:00']
